import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Send,
  Download,
  Copy,
  Eye,
  Users,
  Building2,
  Calendar,
  RefreshCw,
  Code,
  FileImage,
  Settings2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import debounce from "lodash/debounce";
import CategoryManager from "../components/CategoryManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_TYPES = [
  { value: "work_notice", label: "Work Notice" },
  { value: "invoice", label: "Invoice" },
  { value: "quote", label: "Quote / Estimate" },
  { value: "contract", label: "Contract / Agreement" },
  { value: "custom", label: "Custom Document" },
];

const OUTPUT_FORMATS = [
  { value: "pdf", label: "PDF" },
  { value: "docx", label: "Word (DOCX)" },
  { value: "html", label: "HTML" },
];

const DocumentTemplates = () => {
  const { isOfficeManager } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [propertyManagers, setPMs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // Form state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    template_type: "work_notice",
    content: "",
    category: "",
    output_format: "pdf",
  });
  
  // Preview state
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [categories, setCategories] = useState([]);
  
  // Generate state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientType, setRecipientType] = useState("company");
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [generatedDocs, setGeneratedDocs] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced preview update
  const updatePreview = useCallback(
    debounce(async (content) => {
      if (!content) {
        setPreviewHtml("");
        return;
      }
      setPreviewLoading(true);
      try {
        const response = await axios.post(`${API}/templates/preview`, {
          content,
          output_format: "html",
        });
        setPreviewHtml(response.data.preview);
      } catch (error) {
        console.error("Preview error:", error);
      } finally {
        setPreviewLoading(false);
      }
    }, 500),
    []
  );

  // Update preview when content changes
  useEffect(() => {
    if (editDialogOpen && formData.content) {
      updatePreview(formData.content);
    }
  }, [formData.content, editDialogOpen, updatePreview]);

  const fetchData = async () => {
    try {
      const [templatesRes, companiesRes, pmsRes, schedulesRes, placeholdersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/property-managers`),
        axios.get(`${API}/schedules`),
        axios.get(`${API}/templates/placeholders/available`),
        axios.get(`${API}/categories?category_type=Document`).catch(() => ({ data: [] })),
      ]);
      setTemplates(templatesRes.data);
      setCompanies(companiesRes.data);
      setPMs(pmsRes.data);
      setSchedules(schedulesRes.data);
      setPlaceholders(placeholdersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        description: template.description || "",
        template_type: template.type || template.template_type || "work_notice",
        content: template.content,
        category: template.category || "",
        output_format: template.output_format || "pdf",
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: "",
        description: "",
        template_type: "work_notice",
        content: getDefaultTemplate("work_notice"),
        category: "",
        output_format: "pdf",
      });
    }
    setPreviewHtml("");
    setEditDialogOpen(true);
  };

  const getDefaultTemplate = (type) => {
    const defaults = {
      work_notice: `# WORK NOTICE

## Company Information
**From:** {{company_name}}
**Phone:** {{company_phone}}
**Email:** {{company_email}}

## Property Information
**Address:** {{property_address}}
**Strata Plan:** {{strata_number}}
**Building Contact:** {{building_contact}}

## Schedule Details
**Date(s):** {{job_date}}
**Time:** {{job_start_time}} - {{job_end_time}}
**Duration:** {{estimated_hours}} hours
**Crew Size:** {{crew_size}} workers
**Assigned Team:** {{assigned_employees}}

## Work Description
{{job_description}}

Services to be performed:
{{quote_services}}

## Safety Information
{{safety_requirements}}

## Contact
For questions or to reschedule, please contact:
**Phone:** {{company_phone}}
**Email:** {{company_email}}

---
{{company_name}}`,

      invoice: `# INVOICE

**Invoice #:** {{invoice_number}}
**Invoice Date:** {{current_date}}
**Due Date:** {{due_date}}
**Quote Reference:** {{quote_number}}

---

## From:
**{{company_name}}**
{{company_address}}
Phone: {{company_phone}}
Email: {{company_email}}
GST #: {{company_gst_number}}

## Bill To:
**{{client_company}}**
{{client_address}}
Attn: {{client_name}}
Email: {{client_email}}

---

## Services Rendered:
{{quote_services}}

## Materials/Goods:
{{quote_goods}}

---

| Description | Amount |
|-------------|--------|
| Services Subtotal | {{services_total}} |
| Goods Subtotal | {{goods_total}} |
| **Subtotal** | {{subtotal}} |
| GST ({{gst_rate}}%) | {{gst_amount}} |
| PST ({{pst_rate}}%) | {{pst_amount}} |
| **TOTAL DUE** | **{{quote_total}}** |

---

## Payment Terms
Payment due within {{payment_terms}} days of invoice date.

**Payment Methods:**
- E-Transfer to {{company_email}}
- Cheque payable to {{company_name}}

---
Thank you for your business!
{{company_name}}`,

      quote: `# QUOTATION

**Quote #:** {{quote_number}}
**Date:** {{quote_date}}
**Valid Until:** {{quote_expiry_date}}

---

## From:
**{{company_name}}**
{{company_address}}
Phone: {{company_phone}}
Email: {{company_email}}

## Prepared For:
**{{client_company}}**
{{client_address}}
Attn: {{client_name}}
Phone: {{client_phone}}
Email: {{client_email}}

---

## Property Details:
**Address:** {{property_address}}
**Building Type:** {{building_type}}
**Floors:** {{num_floors}}
**Units:** {{num_units}}

## Scope of Work:
{{job_description}}

---

## Services:
{{quote_services}}

## Materials/Products:
{{quote_goods}}

---

| Item | Amount |
|------|--------|
| Services | {{services_total}} |
| Materials | {{goods_total}} |
| **Subtotal** | {{subtotal}} |
| Taxes | {{tax_amount}} |
| **TOTAL** | **{{quote_total}}** |

---

## Terms & Conditions:
- Quote valid for 30 days from date issued
- 50% deposit required to book
- Payment due upon completion

## Acceptance:
To accept this quote, please sign below:

Signature: _________________________ Date: _________

---
{{company_name}}`,

      contract: `# SERVICE AGREEMENT

**Agreement #:** {{contract_number}}
**Date:** {{current_date}}

---

## Parties:
**Service Provider:**
{{company_name}}
{{company_address}}

**Client:**
{{client_company}}
{{client_address}}
Contact: {{client_name}}

---

## Service Location:
{{property_address}}

## Services to be Provided:
{{quote_services}}

## Schedule:
**Start Date:** {{start_date}}
**End Date:** {{end_date}}
**Frequency:** {{service_frequency}}

## Pricing:
**Total Contract Value:** {{quote_total}}
**Payment Terms:** {{payment_terms}}

---

## Terms and Conditions:

1. **Scope of Work:** As detailed above
2. **Payment:** Due as per payment terms
3. **Cancellation:** 24-hour notice required
4. **Insurance:** Provider maintains liability insurance
5. **Warranty:** Work guaranteed for 30 days

---

## Signatures:

**Service Provider:**
Name: _________________________ 
Date: _________________________

**Client:**
Name: _________________________
Date: _________________________

---
{{company_name}}`,

      custom: `# {{document_title}}

**Date:** {{current_date}}
**Reference:** {{reference_number}}

---

## From:
**{{company_name}}**
{{company_address}}
Phone: {{company_phone}}
Email: {{company_email}}

## To:
**{{client_company}}**
{{client_address}}
Attn: {{client_name}}

---

## Content:
[Your content here]

---

## Available Placeholders:
- Company: {{company_name}}, {{company_address}}, {{company_phone}}, {{company_email}}
- Client: {{client_name}}, {{client_company}}, {{client_address}}, {{client_email}}, {{client_phone}}
- Property: {{property_address}}, {{num_floors}}, {{num_units}}
- Quote: {{quote_number}}, {{quote_total}}, {{quote_services}}, {{quote_goods}}
- Dates: {{current_date}}, {{job_date}}, {{due_date}}
- Taxes: {{gst_rate}}, {{pst_rate}}, {{gst_amount}}, {{pst_amount}}

---
{{company_name}}`,
    };
    return defaults[type] || defaults.custom;
  };

  const handleSaveTemplate = async () => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        type: formData.template_type,
        content: formData.content,
        category: formData.category || null,
        output_format: formData.output_format,
      };
      
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, payload);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/templates`, payload);
        toast.success("Template created");
      }
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/templates/${templateId}/duplicate`);
      toast.success("Template duplicated");
      fetchData();
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById("template-content");
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent =
      formData.content.substring(0, start) +
      placeholder +
      formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
  };

  const handleOpenGenerateDialog = (template) => {
    setSelectedTemplate(template);
    setSelectedRecipients([]);
    setGeneratedDocs(null);
    setGenerateDialogOpen(true);
  };

  const toggleRecipient = (id) => {
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const selectAllRecipients = () => {
    const allIds = (recipientType === "company" ? companies : propertyManagers).map((r) => r.id);
    setSelectedRecipients(allIds);
  };

  const handleGenerate = async () => {
    if (selectedRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/documents/generate`, {
        template_id: selectedTemplate.id,
        recipient_ids: selectedRecipients,
        recipient_type: recipientType,
        schedule_id: selectedSchedule || null,
      });
      setGeneratedDocs(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to generate documents");
    } finally {
      setGenerating(false);
    }
  };

  const handleSendBulk = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/documents/send-bulk`, {
        template_id: selectedTemplate.id,
        recipient_ids: selectedRecipients,
        recipient_type: recipientType,
        schedule_id: selectedSchedule || null,
      });
      toast.success(`Sent ${response.data.total_sent} documents`);
      setPreviewDialogOpen(false);
      setGenerateDialogOpen(false);
    } catch (error) {
      toast.error("Failed to send documents");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadSingle = async (recipientId) => {
    try {
      const response = await axios.post(
        `${API}/documents/generate-and-download/${recipientId}?template_id=${selectedTemplate.id}&recipient_type=${recipientType}${selectedSchedule ? `&schedule_id=${selectedSchedule}` : ""}`,
        {},
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `document_${recipientId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  if (!isOfficeManager) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Only Office Managers can access this feature</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Document Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Create templates for work notices, invoices, and other documents with auto-fill placeholders.
          </p>
        </div>
        <Button onClick={() => handleOpenEditDialog()} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No templates yet</p>
            <Button
              variant="link"
              onClick={() => handleOpenEditDialog()}
              className="mt-2"
            >
              Create your first template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {template.description || "No description"}
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {TEMPLATE_TYPES.find((t) => t.value === (template.type || template.template_type))?.label || template.type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span><span className="font-medium">{template.variables?.length || 0}</span> placeholders</span>
                  {template.category && (
                    <Badge variant="secondary" className="text-xs">{template.category}</Badge>
                  )}
                  {template.output_format && (
                    <span className="uppercase">{template.output_format}</span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEditDialog(template)}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenGenerateDialog(template)}
                    data-testid={`generate-${template.id}`}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Generate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateTemplate(template.id)}
                    title="Duplicate template"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              Use placeholders like {"{{client_name}}"} to auto-fill information. Preview updates in real-time.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 lg:grid-cols-2 max-h-[65vh] overflow-hidden">
            {/* Editor Column */}
            <div className="space-y-4 overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Template Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Work Notice Template"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select
                    value={formData.template_type}
                    onValueChange={(v) => setFormData({ ...formData, template_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Category</Label>
                  <Select
                    value={formData.category || "none"}
                    onValueChange={(v) => setFormData({ ...formData, category: v === "none" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Output Format</Label>
                  <Select
                    value={formData.output_format}
                    onValueChange={(v) => setFormData({ ...formData, output_format: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OUTPUT_FORMATS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template"
                />
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Template Content *</Label>
                  <div className="flex items-center gap-2">
                    <Code className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Markdown supported</span>
                  </div>
                </div>
                <Textarea
                  id="template-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                  placeholder="Enter template content with {{placeholders}}..."
                />
              </div>
              
              {/* Placeholders */}
              <div className="space-y-2 border-t pt-3">
                <Label className="text-xs font-medium">Insert Placeholder</Label>
                <div className="space-y-3 max-h-32 overflow-y-auto">
                  {Object.entries(placeholders).map(([category, items]) => (
                    <div key={category} className="space-y-1">
                      <p className="text-xs font-medium uppercase text-muted-foreground">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {items.map((p) => (
                          <Button
                            key={p}
                            variant="outline"
                            size="sm"
                            className="text-xs h-6 px-2"
                            onClick={() => insertPlaceholder(p)}
                          >
                            {p.replace(/\{\{|\}\}/g, '')}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Preview Column */}
            <div className="border rounded-lg overflow-hidden flex flex-col bg-muted/30">
              <div className="flex items-center justify-between px-3 py-2 bg-muted border-b">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm font-medium">Live Preview</span>
                </div>
                <div className="flex items-center gap-2">
                  {previewLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updatePreview(formData.content)}
                    className="h-7"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-white">
                {previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-full min-h-[400px] border-0"
                    title="Template Preview"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground p-8">
                    <div className="text-center">
                      <FileImage className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Start typing to see preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Documents Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Documents</DialogTitle>
            <DialogDescription>
              Select recipients to generate individualized documents from "{selectedTemplate?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Recipient Type */}
            <div className="space-y-2">
              <Label>Recipient Type</Label>
              <Select value={recipientType} onValueChange={(v) => {
                setRecipientType(v);
                setSelectedRecipients([]);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Companies</SelectItem>
                  <SelectItem value="property_manager">Property Managers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Optional Schedule */}
            <div className="space-y-2">
              <Label>Link to Schedule (optional)</Label>
              <Select value={selectedSchedule} onValueChange={setSelectedSchedule}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a schedule for date info" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {schedules.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.confirmed_date || s.proposed_dates?.[0] || "No date"} - {s.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Recipients */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Recipients ({selectedRecipients.length} selected)</Label>
                <Button variant="ghost" size="sm" onClick={selectAllRecipients}>
                  Select All
                </Button>
              </div>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {(recipientType === "company" ? companies : propertyManagers).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center space-x-3 p-3 border-b last:border-0 hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={selectedRecipients.includes(r.id)}
                      onCheckedChange={() => toggleRecipient(r.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.email || r.accounts_payable_email || "No email"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || selectedRecipients.length === 0}
              data-testid="generate-docs-btn"
            >
              {generating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Eye className="h-4 w-4 mr-2" />
              Preview Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview & Send Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Preview</DialogTitle>
            <DialogDescription>
              Review generated documents before sending. {generatedDocs?.total_documents} document(s) ready.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {generatedDocs?.documents?.map((doc, index) => (
              <Card key={doc.recipient_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{doc.recipient_name}</CardTitle>
                      <CardDescription>{doc.recipient_email || "No email"}</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadSingle(doc.recipient_id)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-muted/50 rounded-lg max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {doc.preview_content}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex-1 text-sm text-muted-foreground">
              Documents will be sent to recipients with valid email addresses
            </div>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBulk} disabled={sending} data-testid="send-all-btn">
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" />
              Send All ({generatedDocs?.total_documents})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentTemplates;
